import { useMemo } from 'react';
import { SalesTermsMapper } from 'shared/mappers';
import { ProductsGridSkeleton } from 'src/components/containers/skeleton/ProductsGridSkeleton';
import SkeletonEntryForm from 'src/components/containers/skeleton/SkeletonEntryForm';
import { AgCollapsibleCard, AgText } from 'src/components/ui';

import { CustomerPortalPurchaseOrderData } from '../purchase-order/useCustomerPortalPurchaseOrder';
import useCustomerPortalLocations from '../useCustomerPortalLocations';

type Props = { purchaseOrder: CustomerPortalPurchaseOrderData };

export default function OrderDetails({ purchaseOrder }: Props) {
  const locations = useCustomerPortalLocations();

  const shipTo = useMemo(
    () => locations?.shipping?.find((l) => l.id === purchaseOrder?.shipment?.destinationId)?.label ?? '--',
    [locations, purchaseOrder]
  );

  const billTo = useMemo(
    () => locations?.billing?.find((l) => l.id === purchaseOrder?.billToId)?.label ?? '--',
    [locations, purchaseOrder]
  );

  if (!purchaseOrder) {
    return (
      <div className="border border-neutral-5 rounded-lg bg-white p-4">
        <SkeletonEntryForm fields={5} />
        <ProductsGridSkeleton />
      </div>
    );
  }

  return (
    <AgCollapsibleCard title="Order Details">
      <div className="flex flex-wrap gap-4">
        <Field label="Customer" displayValue={purchaseOrder.businessEntity?.name} />
        <Field label="Order date" displayValue={purchaseOrder.orderDate} />
        <Field label="Ship to" displayValue={shipTo} />
        <Field label="Bill to" displayValue={billTo} />
        <Field label="Sales terms" displayValue={SalesTermsMapper.getLabel(purchaseOrder.salesTerms)} />
      </div>
    </AgCollapsibleCard>
  );
}

const Field = ({ label, displayValue }) => (
  <div className="w-full sm:w-[calc(50%-8px)] md:w-[calc(33.333%-11px)]">
    <div className="flex flex-col">
      <AgText size="12" className="uppercase tracking-wider text-neutral-8">
        {label}
      </AgText>
      <AgText size="16">{displayValue}</AgText>
    </div>
  </div>
);
